"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { LINK, ROW_ON } from "@/lib/ui-classes";
import { Landmark, ExternalLink, ChevronDown, ChevronRight, CalendarDays, FileText, Check, X } from "lucide-react";
import Link from "@/lib/localized-link";
import BottomSheet from "@/components/BottomSheet";
import { formatPrice, trackCalcEvent } from "@/lib/api";
import { noteEntry } from "@/lib/entry-context";
import { useT, useLocale } from "@/lib/locale-context";

import { digitsOnly, groupDigits } from "@/lib/number-input";
import { Field, DownPaymentField } from "@/components/calc/Field";
import { readAffordabilityProfile, lenderCeiling, BANK_LOAN_CAP } from "@/lib/affordability";
// The customer-affordability rule: a mortgage payment shouldn't eat more
// than this share of monthly income. Used to pick the optimal term.
const MAX_DTI = 0.7;
// Official Azerbaijan living minimum for the working-age population,
// 2026 (yaşayış minimumu, əmək qabiliyyətli əhali) - president.az law of
// 2026, effective 1 Jan 2026. The optimal term must leave at least this
// much to live on, not just clear the 70% rule. Update annually.
const LIVING_MIN = 317; // AZN / month
// Sane input ceilings - keep values in plausible ranges (no negatives,
// no billion-AZN mortgages).
const MAX_PRICE = 100_000_000; // AZN
const MAX_INCOME = 1_000_000; // AZN / month
const MAX_RATE = 100; // %
// Standard milestones shown for context, shortest first. The actual
// recommendation is the exact shortest affordable year (1..30), inserted
// into this list - you can pay a mortgage off in well under 10 years.
const BASE_TERMS = [5, 10, 15, 20, 25, 30];
// Hero presets (board 3d): the three terms people actually weigh.
const HERO_TERMS = [15, 20, 25];
// 30y is the preferential (güzəştli) mortgage ceiling; matching it here
// keeps this calculator consistent with the eligibility checker, which
// can recommend up to a 30-year term for that track.
const MAX_TERM_YEARS = 30;

// The Fund's official one-time cost schedule (İpoteka ilə bağlı xərclər).
// Notary state duty 60-150 AZN (güzəştli-adi, Bakı) + registration ~80 AZN;
// appraisal 170-260 AZN (we show a representative figure); insurance scales
// with the loan (life+disability 0.26-2% + property 0.2-0.22%).
const COSTS_PDF = "https://mcgf.gov.az/storage/punk-pdf/01JXHMXEQP913NZJ91KNCD3Q11.pdf";
const COST_NOTARY_REG: readonly [number, number] = [140, 230];
const COST_APPRAISAL = 200;
const INS_LOW = 0.0046;  // 0.26% life + 0.20% property
const INS_HIGH = 0.0222; // 2.00% life + 0.22% property

// Month names authored per locale: browser ICU does not ship Azerbaijani
// long month names, so Intl falls back to "M06". Index 0 = January.
const MONTH_NAMES: Record<string, readonly string[]> = {
  az: ["Yanvar", "Fevral", "Mart", "Aprel", "May", "İyun", "İyul", "Avqust", "Sentyabr", "Oktyabr", "Noyabr", "Dekabr"],
  ru: ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

/** Custom month/year dropdown for the schedule start date. A native <select>
 *  stays styled when closed but opens the plain OS list; this renders a fully
 *  branded popover (grey selected row with a check, hover states, dark-mode safe via
 *  bg-white + semantic grays). Closes on outside-click / Escape and scrolls the
 *  selected month into view on open. */
function MonthPicker({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    selectedRef.current?.scrollIntoView({ block: "center" });
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-2 text-xs font-semibold capitalize text-gray-900 transition-colors hover:border-gray-400 focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-100"
      >
        {current?.label ?? value}
        <ChevronDown
          className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-30 mt-1.5 max-h-60 w-44 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg shadow-black/10"
        >
          {options.map((o) => {
            const isSel = o.value === value;
            return (
              <button
                key={o.value}
                ref={isSel ? selectedRef : undefined}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium capitalize transition-colors ${
                  isSel
                    ? ROW_ON
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {o.label}
                {isSel && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Compact thousands for the dense term table, localized: AZ uses "min"
 *  (thousand), RU "тыс", EN "k". 132 000 → "132 min" / "132 тыс" / "132k". */
function kfmt(n: number, locale: string): string {
  const v = Math.round(n);
  if (v < 1000) return String(v);
  const k = Math.round(v / 1000);
  if (locale === "az") return `${k} min`;
  if (locale === "ru") return `${k} тыс`;
  return `${k}k`;
}

/** Mortgage calculator, framed around the buyer's question - not the
 *  bank's. Given income and the 70%-of-income affordability rule, it
 *  recommends the SHORTEST term you can afford (least total interest) and
 *  shows every standard term side by side so the trade-off is visible.
 *  Updates live; rate is a plain editable field with a neutral default
 *  (no AZ "official" rates baked in). Logs use to admin activity. */
/** sm breakpoint probe - decides whether the payment schedule opens
 *  as a bottom sheet (phone) or a centered modal (desktop). Same
 *  pattern as SortPicker's. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function MortgageCalculator() {
  const t = useT();
  const { locale } = useLocale();

  // Starts EMPTY (owner, 2026-09-16): a prefilled 100 000 AZN produced a
  // monthly payment nobody asked for. The result column shows the hint
  // until a price is typed or dragged in.
  const [priceStr, setPriceStr] = useState("");
  // Down payment can be entered as a % of price OR an absolute AZN
  // amount; `downMode` picks which, and `downStr` is the raw entry in
  // that unit. The two views stay in sync via the helper line + the
  // unit toggle.
  const [downStr, setDownStr] = useState("30");
  const [downMode, setDownMode] = useState<"pct" | "abs">("pct");
  const [rateStr, setRateStr] = useState("11");
  const [incomeStr, setIncomeStr] = useState("");
  // Optional user-chosen term: the table always shows the milestones +
  // the optimal, but "what about exactly 12 years?" needs its own row.

  // Hero term (board 3d): the one-number monthly payment leads with a
  // preset switch - answers immediately, income optional.
  const [heroTerm, setHeroTerm] = useState(20);
  // The table badges a row as the reader's own only once they have
  // actually set a term; until then 20 years is just the default.
  const [termPicked, setTermPicked] = useState(false);
  const chooseTerm = (y: number) => { setTermPicked(true); setHeroTerm(y); };
  // Amortization schedule: collapsed by default; start month seeds the dates.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // PDF export failed (font/logo fetch or jspdf) - shows a brief error
  // under the export button instead of an unhandled rejection.
  const [pdfErr, setPdfErr] = useState(false);
  const [startMonth, setStartMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Prefill from URL query params when arriving from the eligibility
  // checker: it passes the resolved track rate (4% güzəştli / 8% adi)
  // plus the user's price, down-payment % and income, so the two tools
  // show a consistent recommendation. Read from window.location on mount
  // rather than useSearchParams to avoid forcing a Suspense boundary
  // (same rationale as SharedHeader). Runs once.
  //
  // When a prefill is applied, the queued setStates only land on the
  // NEXT render - the usage-logging baseline below must not be captured
  // from the pre-prefill (default) render, or the prefill itself would
  // read as a user edit and log a phantom calc event on leave. This
  // flag tells the snapshot effect to defer the baseline one render.
  const prefillPending = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const num = (k: string) => {
      const v = q.get(k);
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : null;
    };
    const p = num("price");
    if (p != null) setPriceStr(String(Math.round(p)));
    // A prefilled page lands on the answer (owner, 2026-10-11).
    if (p != null) window.setTimeout(() => document.getElementById("calc-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    const dn = num("down");
    if (dn != null) {
      setDownMode("pct");
      setDownStr(String(Math.round(dn)));
    }
    const r = num("rate");
    if (r != null) setRateStr(String(r));
    const inc = num("income");
    if (inc != null) setIncomeStr(String(Math.round(inc)));
    // No URL values: the budget profile the person already filled in
    // (income, savings as the down payment) stands in, so the two tools
    // agree without retyping (owner, 2026-09-21).
    const profile = readAffordabilityProfile();
    let fromProfile = false;
    if (inc == null && profile?.income) {
      setIncomeStr(String(Math.round(profile.income + (profile.coIncome ?? 0))));
      fromProfile = true;
    }
    if (dn == null && profile?.savings) {
      setDownMode("abs");
      setDownStr(String(Math.round(profile.savings)));
      fromProfile = true;
    }
    if (p != null || dn != null || r != null || inc != null || fromProfile) {
      prefillPending.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clamp to plausible ranges defensively (the inputs already clamp on
  // entry, but the defaults / programmatic values flow through here too).
  const price = Math.min(MAX_PRICE, Math.max(0, Number(priceStr) || 0));
  const ratePct = Math.min(MAX_RATE, Math.max(0, Number(rateStr) || 0));
  const income = Math.min(MAX_INCOME, Math.max(0, Number(incomeStr) || 0));
  // The term the result's own slider stands on. It used to be a field in
  // the input column as well, which asked the same question twice (owner,
  // 2026-09-21); the slider is the single control and the terms table
  // carries its row.
  // Only a term the reader actually picked joins the table; the suggested
  // year is inserted separately, so the suggestion cannot depend on itself.
  const customYears = termPicked
    ? Math.min(MAX_TERM_YEARS, Math.max(1, Math.round(heroTerm))) || null
    : null;
  // Resolve the down payment into both representations from whichever
  // unit the user is entering. The absolute amount can't exceed price;
  // the percentage can't exceed 100.
  const downRaw = Math.max(0, Number(downStr) || 0);
  const downAmount =
    downMode === "abs"
      ? Math.min(price, downRaw)
      : (price * Math.min(100, downRaw)) / 100;
  const downPct = price > 0 ? (downAmount / price) * 100 : 0;

  const switchDownMode = (next: "pct" | "abs") => {
    if (next === downMode) return;
    // Convert the visible value so the field reads the same money in the
    // new unit.
    if (next === "abs") setDownStr(String(Math.round(downAmount)));
    else setDownStr(price > 0 ? String(Math.round(downPct)) : "0");
    setDownMode(next);
  };

  // The terms table renders before a price is entered too, so the result
  // column has one shape (owner, 2026-09-21: "it jumps really drastically
  // as soon as I enter a value"). Its own term list is real; the money
  // columns wait.
  const placeholderRows = useMemo(
    () => [...BASE_TERMS, customYears].filter((y, i, a): y is number => y != null && a.indexOf(y) === i)
      .sort((a, b) => a - b)
      .map((years) => ({ years, monthly: 0, total: 0, totalInterest: 0, pct: null as number | null, over70: false, tight: false })),
    [customYears],
  );

  const calc = useMemo(() => {
    const principal = Math.max(0, price - downAmount);
    if (price <= 0 || principal <= 0) return null;
    const r = ratePct / 100 / 12;
    const monthlyFor = (years: number) => {
      const n = years * 12;
      return r === 0 ? principal / n : (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1);
    };
    // The optimal term must clear BOTH constraints, not just the 70%
    // rule: (a) payment <= 70% of income, and (b) what's left after the
    // payment is at least the living minimum. For low incomes the
    // survival floor binds tighter than 70%, so a term that technically
    // passes 70% but leaves you below subsistence is NOT recommended.
    const cap70 = income > 0 ? MAX_DTI * income : null;
    const survivalCap = income > 0 ? income - LIVING_MIN : null;
    const effCap =
      cap70 != null && survivalCap != null ? Math.min(cap70, survivalCap) : null;
    // Exact shortest term (1..30y) that clears the effective cap. Monthly
    // falls as the term lengthens, so the first year that fits is the
    // optimal one - it can be as low as 1 year, not floored at 10.
    let recYears: number | null = null;
    if (effCap != null && effCap > 0) {
      for (let y = 1; y <= MAX_TERM_YEARS; y++) {
        if (monthlyFor(y) <= effCap) {
          recYears = y;
          break;
        }
      }
    }
    // Standard milestones + the exact recommended year, so the optimal is
    // always represented in the array (even if it's, say, 4 years).
    const years = [...new Set([
      ...BASE_TERMS,
      ...(recYears ? [recYears] : []),
      ...(customYears ? [customYears] : []),
    ])].sort((a, b) => a - b);
    const rows = years.map((y) => {
      const monthly = monthlyFor(y);
      const totalInterest = monthly * y * 12 - principal;
      const pct = income > 0 ? (monthly / income) * 100 : null;
      const remaining = income - monthly;
      // over70: breaks the 70% rule. tight: passes 70% but leaves less
      // than the living minimum to survive on.
      const over70 = cap70 != null && monthly > cap70;
      const tight = !over70 && income > 0 && remaining < LIVING_MIN;
      return { years: y, monthly, totalInterest, pct, remaining, over70, tight };
    });
    const recommended =
      recYears != null ? rows.find((row) => row.years === recYears) ?? null : null;
    return { principal, rows, recommended, hasIncome: income > 0 };
  }, [price, downAmount, ratePct, income, customYears]);

  const rec = calc?.recommended ?? null;
  // Until the reader picks, the card stands on the suggested term; after
  // that their own choice wins and the suggestion becomes an offer again.
  const effectiveTerm = termPicked ? heroTerm : (rec?.years ?? heroTerm);

  // Usage logging: record at most ONE mortgage event per page visit, and
  // only if the visitor actually changed an input. It fires when they
  // leave the page (SPA navigation away, tab close, or backgrounding) and
  // reflects the final state - NOT on mount and NOT on every keystroke.
  // The calculator starts with default values, so logging on entry would
  // count every page load; comparing the latest inputs against the
  // initial snapshot ensures we only log real engagement, exactly once.
  const logged = useRef(false);
  // PDF export is logged at most once per page load: re-exporting (e.g. after
  // tweaking the term) should not inflate the analytics count.
  const pdfLogged = useRef(false);
  const initialInputs = useRef<string | null>(null);
  const latestInputs = useRef("");
  const inputs = `${priceStr}|${downStr}|${downMode}|${rateStr}|${incomeStr}|${effectiveTerm}`;
  // True once the user edits ANY input: the page seeds sensible
  // defaults so the live numbers render, but a dated payment plan for
  // values the user never typed reads as fiction - the schedule stays
  // gated until the form is theirs.
  const initialInputsRef = useRef(inputs);
  const inputsDirty = inputs !== initialInputsRef.current;
  useEffect(() => {
    latestInputs.current = inputs;
    if (prefillPending.current) {
      // The prefill effect (declared above, so it ran first this
      // commit) queued setStates that haven't rendered yet. Skip the
      // baseline this render; the next run captures the prefilled
      // values as the "initial" state instead of the defaults.
      prefillPending.current = false;
      initialInputs.current = null;
      return;
    }
    if (initialInputs.current === null) initialInputs.current = inputs;
  });
  useEffect(() => {
    const log = () => {
      if (logged.current) return;
      // No modification from the defaults -> this was just a page view.
      if (initialInputs.current === null || latestInputs.current === initialInputs.current) {
        return;
      }
      logged.current = true;
      // The final settled calculation, WITH its numbers (owner ask:
      // logs must carry the values). Parsed from the same pipe-joined
      // snapshot the modified-check uses.
      const [p, d, dm, r, inc, cy] = latestInputs.current.split("|");
      // The hub may ask "buying up to this price?" (lib/entry-context).
      noteEntry("mortgage", { price: Number(p) || undefined });
      void trackCalcEvent("mortgage", {
        price: Number(p) || null,
        down: Number(d) || null,
        down_mode: dm || null,
        rate: Number(r) || null,
        income: Number(inc) || null,
        custom_years: Number(cy) || null,
      });
    };
    // visibilitychange(hidden) is the reliable "leaving" signal (tab close /
    // switch / background); pagehide backs it up; the cleanup covers in-app
    // (SPA) navigation away. `logged` keeps it to a single write per visit.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") log();
    };
    window.addEventListener("pagehide", log);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", log);
      document.removeEventListener("visibilitychange", onVisibility);
      log();
    };
  }, []);

  // What the suggested term saves against the longest one: the reason the
  // suggestion exists, carried on the same line instead of its own card.
  const recSaving = (() => {
    const worst = calc?.rows.find((r) => r.years === MAX_TERM_YEARS);
    return rec && worst && rec.years < MAX_TERM_YEARS ? worst.totalInterest - rec.totalInterest : 0;
  })();

  // Upfront one-time costs (estimate), from the Fund's official schedule.
  // Insurance scales with the loan; the rest are fixed ranges. Shown
  // whenever there is a principal, independent of income.
  const principalNow = Math.max(0, price - downAmount);
  const insLow = Math.round(principalNow * INS_LOW);
  const insHigh = Math.round(principalNow * INS_HIGH);
  const costLow = COST_NOTARY_REG[0] + insLow + COST_APPRAISAL;
  const costHigh = COST_NOTARY_REG[1] + insHigh + COST_APPRAISAL;

  // Amortization schedule for the active term (the custom term if entered,
  // else the recommended one). Built only when the section is open.
  const scheduleYears = customYears ?? rec?.years ?? null;
  const schedule = useMemo(() => {
    if (!scheduleOpen || !scheduleYears || principalNow <= 0) return null;
    const r = ratePct / 100 / 12;
    const n = scheduleYears * 12;
    const payment =
      r === 0 ? principalNow / n : (principalNow * r * (1 + r) ** n) / ((1 + r) ** n - 1);
    const [yy, mm] = startMonth.split("-").map(Number);
    let balance = principalNow;
    let totalInterest = 0;
    const rows = [];
    for (let i = 0; i < n; i++) {
      const interest = balance * r;
      const principalPart = Math.min(payment - interest, balance);
      totalInterest += interest;
      balance = Math.max(0, balance - principalPart);
      const d = new Date(yy, mm - 1 + i, 1);
      const date = `${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
      rows.push({ i: i + 1, date, payment, principalPart, interest, balance });
    }
    return { rows, payment, totalInterest };
  }, [scheduleOpen, scheduleYears, principalNow, ratePct, startMonth]);

  // Start-month choices for the schedule: this month plus the next 35, each
  // labelled with the localized month + year. Replaces the raw <input month>,
  // whose native widget renders inconsistently and looks unbranded.
  const startOptions = useMemo(() => {
    const now = new Date();
    const names = MONTH_NAMES[locale] ?? MONTH_NAMES.en;
    return Array.from({ length: 36 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      return {
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: `${names[d.getMonth()]} ${d.getFullYear()}`,
      };
    });
  }, [locale]);

  // Branded PDF: embeds a Unicode font (DejaVu - Azerbaijani + Cyrillic) so
  // localized labels render, the brand logo + colours, clickable product and
  // legal-source links, and a clean striped schedule table (no heat-map).
  // jspdf + the font/logo assets are fetched lazily, only on export.
  const exportPdf = async () => {
    if (!schedule) return;
    setPdfErr(false);
    try {
      await buildAndSavePdf(schedule);
    } catch (err) {
      // Asset fetch / jspdf failure: surface a brief localized error
      // instead of crashing with an unhandled rejection.
      console.error("mortgage PDF export failed", err);
      setPdfErr(true);
    }
  };

  const buildAndSavePdf = async (sched: NonNullable<typeof schedule>) => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;

    const toB64 = (buf: ArrayBuffer) => {
      const bytes = new Uint8Array(buf);
      let bin = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      return btoa(bin);
    };
    const blobToDataUrl = (blob: Blob) =>
      new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error ?? new Error("FileReader failed"));
        r.readAsDataURL(blob);
      });
    // A 404/500 on the font or logo must fail the export loudly (the
    // caller shows the error), not feed an HTML error page into jspdf.
    const fetchOk = async (path: string) => {
      const r = await fetch(path);
      if (!r.ok) throw new Error(`${path}: HTTP ${r.status}`);
      return r;
    };

    const [reg, bold, logo] = await Promise.all([
      fetchOk("/fonts/DejaVuSans.ttf").then((r) => r.arrayBuffer()),
      fetchOk("/fonts/DejaVuSans-Bold.ttf").then((r) => r.arrayBuffer()),
      fetchOk("/logo-white.png").then((r) => r.blob()).then(blobToDataUrl),
    ]);

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.addFileToVFS("DejaVuSans.ttf", toB64(reg));
    doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
    doc.addFileToVFS("DejaVuSans-Bold.ttf", toB64(bold));
    doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
    doc.setFont("DejaVu", "normal");

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    // Web-brand palette: indigo-600 is the single product accent, and the
    // header stripe runs indigo→violet to mirror the site's --grad-brand-stripe.
    const INDIGO: [number, number, number] = [79, 70, 229]; // indigo-600
    const VIOLET: [number, number, number] = [124, 58, 237]; // violet-600
    const INK: [number, number, number] = [33, 33, 48];
    const MUTE: [number, number, number] = [125, 125, 140];
    const ALT: [number, number, number] = [238, 242, 255]; // indigo-50
    const money = (n: number) => Math.round(n).toLocaleString("en-US");
    const fmt = (n: number) => `${money(n)} AZN`;

    // ── Header: indigo band + indigo→violet accent stripe + logo + title ──
    doc.setFillColor(...INDIGO);
    doc.rect(0, 0, pageW, 82, "F");
    const stripeSteps = 96;
    const sliceW = pageW / stripeSteps;
    for (let i = 0; i < stripeSteps; i++) {
      const k = i / (stripeSteps - 1);
      doc.setFillColor(
        Math.round(INDIGO[0] + (VIOLET[0] - INDIGO[0]) * k),
        Math.round(INDIGO[1] + (VIOLET[1] - INDIGO[1]) * k),
        Math.round(INDIGO[2] + (VIOLET[2] - INDIGO[2]) * k),
      );
      doc.rect(i * sliceW, 82, sliceW + 1, 4, "F");
    }
    doc.addImage(logo, "PNG", 40, 22, 40, 40);
    doc.setTextColor(255, 255, 255);
    doc.setFont("DejaVu", "bold");
    doc.setFontSize(19);
    doc.text("Mənzil.ai", 92, 40);
    doc.setFont("DejaVu", "normal");
    doc.setFontSize(12);
    doc.text(t("mortgage.calc.pdf.title"), 92, 60);

    // ── Summary (label : value) ──
    const summary: [string, string][] = [
      [t("mortgage.calc.pdf.loan"), fmt(principalNow)],
      [t("mortgage.calc.rate"), `${Math.round(ratePct * 10) / 10}%`],
      [t("mortgage.calc.col.term"), `${scheduleYears} ${t("mortgage.calc.years")}`],
      [t("mortgage.calc.col.monthly"), fmt(sched.payment)],
      [t("mortgage.calc.pdf.totalinterest"), fmt(sched.totalInterest)],
    ];
    let sy = 112;
    doc.setFontSize(10);
    for (const [label, value] of summary) {
      doc.setFont("DejaVu", "normal");
      doc.setTextColor(...MUTE);
      doc.text(label, 40, sy);
      doc.setFont("DejaVu", "bold");
      doc.setTextColor(...INK);
      doc.text(value, 220, sy);
      sy += 16;
    }

    // ── Schedule table (striped, no heat-map) ──
    autoTable(doc, {
      startY: sy + 12,
      head: [[
        "#",
        t("mortgage.calc.schedule.col.date"),
        t("mortgage.calc.schedule.col.payment"),
        t("mortgage.calc.schedule.col.principal"),
        t("mortgage.calc.schedule.col.interest"),
        t("mortgage.calc.schedule.col.balance"),
        t("mortgage.calc.schedule.paidoff"),
      ]],
      body: sched.rows.map((r) => {
        const paidOff = principalNow > 0 ? ((principalNow - r.balance) / principalNow) * 100 : 0;
        return [
          String(r.i),
          r.date,
          money(r.payment),
          money(r.principalPart),
          money(r.interest),
          money(r.balance),
          `${paidOff.toFixed(0)}%`,
        ];
      }),
      margin: { top: 96, bottom: 44, left: 40, right: 40 },
      styles: { font: "DejaVu", fontSize: 7.5, cellPadding: 3, textColor: INK, halign: "left" },
      headStyles: { font: "DejaVu", fontStyle: "bold", fillColor: INDIGO, textColor: [255, 255, 255], halign: "left" },
      alternateRowStyles: { fillColor: ALT },
      // Everything left-aligned; the "#" column keeps a muted ink.
      columnStyles: {
        0: { textColor: MUTE },
      },
      // Footer on every page: legal source + product link as pill buttons,
      // page number centered between them.
      didDrawPage: (data) => {
        const ph = 16;
        const py = pageH - 34;
        const padX = 8;
        const pillButton = (label: string, url: string, side: "left" | "right") => {
          doc.setFont("DejaVu", "normal");
          doc.setFontSize(7.5);
          const w = doc.getTextWidth(label) + padX * 2;
          const x = side === "left" ? 40 : pageW - 40 - w;
          doc.setFillColor(...INDIGO);
          doc.roundedRect(x, py, w, ph, ph / 2, ph / 2, "F");
          doc.setTextColor(255, 255, 255);
          doc.text(label, x + padX, py + ph / 2, { baseline: "middle" });
          doc.link(x, py, w, ph, { url });
        };
        pillButton(t("mortgage.calc.pdf.source"), "https://e-qanun.az/framework/33106", "left");
        pillButton(t("mortgage.calc.pdf.calculator"), "https://menzil.ai/calculate/mortgage", "right");
        doc.setFont("DejaVu", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...MUTE);
        doc.text(String(data.pageNumber), pageW / 2, py + ph / 2, {
          align: "center",
          baseline: "middle",
        });
      },
    });

    doc.save(`menzil-ipoteka-${scheduleYears}il.pdf`);
    // Track the PDF export at most once per page load (distinct from the
    // once-per-page calculator-usage event); best-effort, never blocks.
    if (!pdfLogged.current) {
      pdfLogged.current = true;
      void trackCalcEvent("mortgage_pdf");
    }
  };

  const isMobile = useIsMobile();

  // Esc closes the desktop schedule modal (the bottom sheet handles
  // its own Escape + drag-to-close).
  useEffect(() => {
    if (!scheduleOpen || isMobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setScheduleOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scheduleOpen, isMobile]);

  // Shared body for the schedule overlay (bottom sheet on phones,
  // centered modal on desktop): start-month picker + the dated table
  // + the PDF download.
  const scheduleBody =
    scheduleYears == null ? (
      <p className="text-xs text-gray-500">{t("mortgage.calc.schedule.needterm")}</p>
    ) : schedule ? (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[11px] font-semibold text-gray-400">
            {t("mortgage.calc.schedule.for").replace("{years}", String(scheduleYears))}
          </span>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold text-gray-400">
            {t("mortgage.calc.schedule.start")}
            <MonthPicker
              value={startMonth}
              options={startOptions}
              onChange={setStartMonth}
              label={t("mortgage.calc.schedule.start")}
            />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-gray-100">
          <table className="w-full text-xs tabular-nums">
            <thead className="sticky top-0 bg-gray-50 text-[10px] text-gray-400">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-semibold">#</th>
                <th className="px-2 py-1.5 font-semibold">{t("mortgage.calc.schedule.col.date")}</th>
                <th className="px-2 py-1.5 font-semibold">{t("mortgage.calc.schedule.col.payment")}</th>
                <th className="px-2 py-1.5 font-semibold">{t("mortgage.calc.schedule.col.principal")}</th>
                <th className="px-2 py-1.5 font-semibold">{t("mortgage.calc.schedule.col.interest")}</th>
                <th className="px-2 py-1.5 font-semibold">{t("mortgage.calc.schedule.col.balance")}</th>
              </tr>
            </thead>
            <tbody>
              {schedule.rows.map((row) => (
                <tr key={row.i} className="border-t border-gray-100">
                  <td className="px-2 py-1 text-gray-400">{row.i}</td>
                  <td className="px-2 py-1 text-gray-700">{row.date}</td>
                  <td className="px-2 py-1 text-gray-900">{formatPrice(row.payment)}</td>
                  <td className="px-2 py-1 text-gray-500">{formatPrice(row.principalPart)}</td>
                  <td className="px-2 py-1 text-gray-500">{formatPrice(row.interest)}</td>
                  <td className="px-2 py-1 text-gray-700">{formatPrice(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={exportPdf}
            className="group inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 active:scale-[0.98]"
          >
            <FileText className="h-4 w-4 transition-transform" />
            {t("mortgage.calc.schedule.exportpdf")}
          </button>
          {pdfErr && (
            <p className="mt-2 text-xs font-medium text-rose-600">
              {t("mortgage.calc.schedule.exportpdf.err")}
            </p>
          )}
        </div>
      </>
    ) : null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-7">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Landmark className="w-4 h-4 text-gray-400 shrink-0" />
          <h3 className="text-base font-bold text-gray-900 truncate">
            {t("mortgage.calc.title")}
          </h3>
        </div>
        <Link
          href="/blog/guzestli-ipoteka-2026"
          className={`inline-flex items-center gap-1 text-[11px] shrink-0 ${LINK}`}
        >
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          {t("mortgage.calc.learnmore")}
        </Link>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3 lg:gap-8 lg:items-start">
        {/* Inputs - narrower (1/3) than the result side. Below lg the
            page is one column, and a full-width slider for a five-digit
            number read as oversized (owner, 2026-09-20): between sm and
            lg the input blocks pair up in two columns instead. */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-3 lg:col-span-1 lg:gap-y-4">
          <Field
            className="lg:col-span-2"
            size="auto"
            label={t("mortgage.calc.price")}
            value={priceStr}
            onChange={setPriceStr}
            unit="AZN"
            group
            slider={{ min: 0, max: 1_500_000, step: 5_000 }}
            placeholder="0"
            min={0}
            max={MAX_PRICE}
          />
          {/* Income sits second (owner, 2026-09-21: "the monthly salary is
              way more important, place it higher"): it drives the
              suggested term, so it should be asked before the loan
              terms, not after them. */}
          <Field
            className="lg:col-span-2"
            size="auto"
            label={t("mortgage.calc.income")}
            value={incomeStr}
            onChange={setIncomeStr}
            unit="AZN"
            group
            min={0}
            max={MAX_INCOME}
          />
          <DownPaymentField
            className="lg:col-span-2"
            size="auto"
            label={t("mortgage.calc.downpayment")}
            price={price}
            mode={downMode}
            value={downStr}
            onModeChange={switchDownMode}
            onChange={setDownStr}
          />
          <Field
            size="auto"
            label={t("mortgage.calc.rate")}
            value={rateStr}
            onChange={(v) => {
              if (v === "") return setRateStr("");
              const n = Number(v);
              if (Number.isNaN(n)) return;
              setRateStr(String(Math.min(MAX_RATE, Math.max(0, n))));
            }}
            unit="%"
            step="0.1"
            min={0}
            max={MAX_RATE}
            slider={{ min: 3, max: 20, step: 0.1 }}
          />
          {/* The term sits beside the rate (owner, 2026-09-21): both are
              loan terms and neither needs a full row. */}
          <Field
            size="auto"
            label={t("mortgage.calc.col.term")}
            value={String(effectiveTerm)}
            onChange={(v) => {
              const n = Number(v);
              if (!Number.isFinite(n)) return;
              chooseTerm(Math.min(MAX_TERM_YEARS, Math.max(1, Math.round(n))));
            }}
            unit={t("mortgage.calc.years")}
            min={1}
            max={MAX_TERM_YEARS}
            slider={{ min: 1, max: MAX_TERM_YEARS, step: 1 }}
          />
        </div>

        {/* Result - wider (2/3). */}
        <div id="calc-result" className="scroll-mt-20 space-y-4 lg:col-span-2">
          <>
              {/* ONE result block for the whole width (owner,
                  2026-09-21: "we just replace it right in there, not
                  introduce a subcomponent or another component, one that
                  takes the whole place"). The term you pick rewrites the
                  numbers in place; the suggestion is a line inside the
                  same card that sets the term, never a second card. */}
              {(() => {
                const termYears = Math.min(MAX_TERM_YEARS, Math.max(1, effectiveTerm));
                const n = termYears * 12;
                const r = (Number(rateStr) || 0) / 100 / 12;
                const principal = calc?.principal ?? 0;
                const monthly =
                  principal <= 0
                    ? 0
                    : r === 0
                      ? principal / n
                      : (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1);
                const totalInterest = principal <= 0 ? 0 : monthly * n - principal;
                const live = principal > 0;
                const same = rec != null && rec.years === termYears;
                const remaining = calc?.hasIncome ? income - monthly : 0;
                return (
                  <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-semibold text-gray-500">
                        {t("mortgage.calc.hero.monthly")}
                      </span>
                      {same && (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[11px] font-bold text-emerald-700">
                          {t("mortgage.calc.recommendbadge")}
                        </span>
                      )}
                    </div>
                    <div
                      className={`mt-1 text-3xl font-extrabold tabular-nums leading-none ${
                        live ? "text-gray-900" : "text-gray-300"
                      }`}
                    >
                      {formatPrice(Math.round(monthly))}
                      <span className="ml-1 text-base font-semibold text-gray-400">AZN</span>
                      <span className="ml-2 text-sm font-semibold text-gray-400">
                        {termYears} {t("mortgage.calc.years")}
                      </span>
                    </div>
                    {/* The suggestion rewrites this same card. */}
                    <div className="mt-2 min-h-[1.25rem] text-[11px]">
                      {rec == null ? (
                        <span className={calc?.hasIncome ? "text-amber-700" : "text-gray-400"}>
                          {calc?.hasIncome
                            ? t("mortgage.calc.notaffordable.short")
                            : t("mortgage.calc.enterincome")}
                        </span>
                      ) : same ? (
                        <span className="font-semibold text-emerald-700">
                          {t(termPicked ? "mortgage.calc.rec.current" : "mortgage.calc.rec.auto")}
                          {recSaving >= 500 && (
                            <span className="ml-1 font-normal text-gray-500">
                              {t("mortgage.calc.rec.saving").replace(
                                "{n}",
                                formatPrice(Math.round(recSaving / 100) * 100),
                              )}
                            </span>
                          )}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => chooseTerm(rec.years)}
                          className={LINK}
                        >
                          {t("mortgage.calc.rec.switch")
                            .replace("{y}", String(rec.years))
                            .replace("{m}", formatPrice(Math.round(rec.monthly)))}
                        </button>
                      )}
                    </div>
                    {/* A payment the model can compute is not a loan a
                        lender writes: at a state rate the programme caps
                        the amount (owner, 2026-09-21: "there is a maximum
                        of how much you can actually get a mortgage for").
                        Sources in lib/affordability. */}
                    {(() => {
                      const r = Number(rateStr) || 0;
                      const { cap, kind } = lenderCeiling(r);
                      const key =
                        principal > cap
                          ? kind === "state"
                            ? "mortgage.calc.loancap"
                            : "mortgage.calc.loancap.none"
                          : kind === "bank" && principal > BANK_LOAN_CAP.typical
                            ? "mortgage.calc.loancap.rare"
                            : null;
                      if (!key) return null;
                      return (
                        <p className="mt-2 text-[11px] leading-snug text-amber-700">
                          {t(key)
                            .replace("{n}", formatPrice(cap))
                            .replace("{r}", String(r))
                            .replace("{loan}", formatPrice(principal))}
                        </p>
                      );
                    })()}
                    {/* The detail the old result carried, on the one card
                        (owner, 2026-09-21: "we had way more details, how
                        many left or whatever was in there"). */}
                    <dl className="mt-3 grid gap-x-6 gap-y-1 border-t border-gray-100 pt-3 text-xs sm:grid-cols-3">
                      <div className="flex items-baseline justify-between gap-2 sm:block">
                        <dt className="text-gray-500">{t("mortgage.calc.hero.loan")}</dt>
                        <dd className={`font-bold tabular-nums sm:mt-0.5 sm:text-sm ${live ? "text-gray-900" : "text-gray-300"}`}>
                          {formatPrice(principal)} AZN
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2 sm:block">
                        <dt className="text-gray-500">
                          {t("mortgage.calc.hero.interest").replace("{n}", String(termYears))}
                        </dt>
                        <dd className={`font-bold tabular-nums sm:mt-0.5 sm:text-sm ${live ? "text-gray-900" : "text-gray-300"}`}>
                          {formatPrice(Math.round(totalInterest))} AZN
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2 sm:block">
                        <dt className="text-gray-500">{t("mortgage.calc.remaining")}</dt>
                        <dd
                          className={`font-bold tabular-nums sm:mt-0.5 sm:text-sm ${
                            !calc?.hasIncome
                              ? "text-gray-300"
                              : remaining < LIVING_MIN
                                ? "text-amber-700"
                                : "text-gray-900"
                          }`}
                        >
                          {formatPrice(Math.max(0, Math.round(remaining)))} AZN
                        </dd>
                      </div>
                    </dl>
                  </div>
                );
              })()}
              {/* Every term, side by side - see the trade-off. The block
                  reserves the height of the longest possible list (the six
                  standard terms plus the suggested year plus a typed one,
                  eight rows at 33 px), so typing a term the table does not
                  already carry no longer pushes everything below it down
                  (owner, 2026-09-21: "when a user selects their own
                  duration the height changes, which is why the upcoming
                  parts also move a bit"). */}
              <div className="min-h-[20rem]">
                <div className="text-[11px] font-semibold text-gray-400 mb-1.5">
                  {t("mortgage.calc.allterms")}
                </div>
                {/* -mx-2 + px-2 cells: rows get breathing room for the
                    recommended-row highlight while the first/last column
                    text stays optically aligned with the label above. */}
                <table className="-mx-2 w-[calc(100%+1rem)] text-sm tabular-nums">
                  <thead>
                    <tr className="text-[10px] text-gray-400 text-left">
                      <th className="px-2 font-semibold pb-1.5">{t("mortgage.calc.col.term")}</th>
                      <th className="px-2 font-semibold pb-1.5 text-right">{t("mortgage.calc.col.monthly")}, AZN</th>
                      <th className="px-2 font-semibold pb-1.5 text-right">{t("mortgage.calc.col.interest")}, AZN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(calc?.rows ?? placeholderRows).map((row) => {
                      const isRec = rec?.years === row.years;
                      const isPicked = Math.min(MAX_TERM_YEARS, Math.max(1, effectiveTerm)) === row.years;
                      // Every row picks its term (owner, 2026-09-21: "I
                      // don't see any way to revert to the suggested
                      // pricing, or the selection of the period terms"):
                      // the table is the term selector, so switching to
                      // the suggestion and back is one tap either way.
                      return (
                        <tr
                          key={row.years}
                          onClick={() => chooseTerm(row.years)}
                          tabIndex={0}
                          role="button"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              chooseTerm(row.years);
                            }
                          }}
                          className={`cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 ${
                            isPicked ? "bg-gray-100" : isRec ? "bg-emerald-50/50" : ""
                          }`}
                        >
                          <td className="px-2 py-1.5 font-semibold text-gray-900">
                            <span className="inline-flex items-center gap-1.5">
                              {row.years} {t("mortgage.calc.years")}
                              {isRec && (
                                <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[11px] font-bold text-emerald-700">
                                  {t("mortgage.calc.recommendbadge")}
                                </span>
                              )}
                              {termPicked && isPicked && !isRec && (
                                <span className="rounded-full bg-gray-200 px-1.5 py-px text-[11px] font-bold text-gray-800">
                                  {t("mortgage.calc.custombadge")}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={`px-2 py-1.5 text-right ${row.over70 ? "text-gray-400" : "text-gray-900"}`}>
                            {formatPrice(row.monthly)}
                          </td>
                          <td className="px-2 py-1.5 text-right text-gray-400">
                            {kfmt(row.totalInterest, locale)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          </>
        </div>
      </div>

      {/* Upfront one-time costs - separate from the monthly payment. Uses
          the Fund's official cost schedule (PDF); insurance scales with the
          loan, the rest are fixed ranges. Shown whenever there's a loan. */}
      {principalNow > 0 && (
        <details className="group mt-6 [&_summary::-webkit-details-marker]:hidden">
          <summary className="flex w-full cursor-pointer select-none items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-300">
            {t("mortgage.calc.costs.title")}
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-2 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-white sm:px-4 sm:py-3">
          <dl className="grid grid-cols-2 gap-y-px text-sm">
            <dt className="text-gray-500">{t("mortgage.calc.costs.notary")}</dt>
            <dd className="text-right tabular-nums font-medium text-gray-800">
              {COST_NOTARY_REG[0]}-{COST_NOTARY_REG[1]} AZN
            </dd>
            <dt className="text-gray-500">{t("mortgage.calc.costs.insurance")}</dt>
            <dd className="text-right tabular-nums font-medium text-gray-800">
              {formatPrice(insLow)}-{formatPrice(insHigh)} AZN
            </dd>
            <dt className="text-gray-500">{t("mortgage.calc.costs.appraisal")}</dt>
            <dd className="text-right tabular-nums font-medium text-gray-800">~{COST_APPRAISAL} AZN</dd>
            <dt className="mt-1 border-t border-gray-100 pt-1 font-semibold text-gray-900">
              {t("mortgage.calc.costs.total")}
            </dt>
            <dd className="mt-1 border-t border-gray-100 pt-1 text-right text-base font-bold tabular-nums text-gray-900">
              ~{formatPrice(costLow)}-{formatPrice(costHigh)} AZN
            </dd>
          </dl>
          <a
            href={COSTS_PDF}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-1.5 inline-flex items-center gap-1 text-xs ${LINK}`}
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            {t("mortgage.calc.costs.source")}
          </a>
          </div>
        </details>
      )}

      {/* Amortization schedule - the calculator's reason-to-visit: a
          dated, full payment plan for the chosen term. Opens as an
          OVERLAY (bottom sheet on phones, centered modal on desktop)
          instead of expanding inline - the 240-row table pushed the
          affordability note and disclaimer off-screen, and on mobile
          it read as the page breaking. The PDF download rides inside
          the overlay's footer. */}
      {principalNow > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            disabled={!inputsDirty}
            className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-gray-400" />
              {t("mortgage.calc.schedule.toggle")}
            </span>
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </button>

          {isMobile ? (
            <BottomSheet
              open={scheduleOpen}
              onClose={() => setScheduleOpen(false)}
              title={t("mortgage.calc.schedule.toggle")}
            >
              {scheduleBody}
            </BottomSheet>
          ) : (
            scheduleOpen && (
              <div
                className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-label={t("mortgage.calc.schedule.toggle")}
              >
                <div
                  className="absolute inset-0 bg-black/40"
                  onClick={() => setScheduleOpen(false)}
                  aria-hidden
                />
                <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                  <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <CalendarDays className="h-4 w-4 text-gray-400" />
                      {t("mortgage.calc.schedule.toggle")}
                    </span>
                    <button
                      type="button"
                      onClick={() => setScheduleOpen(false)}
                      aria-label={t("a11y.close")}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                    {scheduleBody}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* The 70% rule banner is gone (owner, 2026-09-21: "the disclaimer
          about the 70% looks really ugly"); the page's own "how it works"
          section carries the rule, and the suggestion line says when a
          term does not fit. */}
      <p className="mt-5 text-[10px] text-gray-400 leading-relaxed">
        {t("mortgage.calc.disclaimer")}
      </p>
    </div>
  );
}
